import { useCallback, useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { newRequestId } from "@/components/casino/dice/diceModel";
import { pendingKey, readFlight, validPending } from "./crashModel";

// Session storage holds intent identity/settings, never outcomes or wallet balances.
export default function useCrashGame(userId, refreshWallet) {
  const [round, setRound] = useState(null), [history, setHistory] = useState([]), [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true), [busy, setBusy] = useState(false), [error, setError] = useState(""), [syncError, setSyncError] = useState("");
  const [historyError, setHistoryError] = useState(false);
  const generation = useRef(0), pending = useRef(null), current = useRef(null), activeId = useRef(null), lock = useRef(false), balanceSignature = useRef("");
  const refreshRef = useRef(refreshWallet); refreshRef.current = refreshWallet;
  const key = pendingKey(userId);
  const live = useCallback(gen => gen === generation.current, []);
  const clearPending = useCallback(id => {
    try { if (JSON.parse(sessionStorage.getItem(key))?.request_id === id) sessionStorage.removeItem(key); } catch { /* safe replay remains possible */ }
    if (pending.current?.request_id === id) pending.current = null;
  }, [key]);
  const accept = useCallback((data, gen) => {
    const value = readFlight(data);
    if (!live(gen) || activeId.current !== value.request_id) return null;
    if (current.current?.request_id === value.request_id && current.current.status === "completed" && value.status !== "completed") return current.current;
    current.current = value; setRound(value);
    if (value.status === "not_placed") { clearPending(value.request_id); setSyncError(""); setError("This flight was not placed. Your wallet was not charged for it."); }
    else if (value.status === "completed") {
      clearPending(value.request_id); setSyncError(""); setError("");
      setHistory(rows => [value, ...rows.filter(row => row.request_id !== value.request_id)].slice(0, 30));
    } else if (!pending.current?.exit_requested) setSyncError("");
    if (value.balances && JSON.stringify(value.balances) !== balanceSignature.current) {
      balanceSignature.current = JSON.stringify(value.balances);
      try { Promise.resolve(refreshRef.current?.(value.balances)).catch(() => {}); } catch { /* wallet refresh must never resend a wager */ }
    }
    return value;
  }, [clearPending, live]);
  const loadHistory = useCallback(async () => {
    const gen = generation.current;
    try {
      const { data } = await api.get("/casino/crash/history");
      const rows = data.map(readFlight);
      if (live(gen)) {
        const confirmed = current.current?.status === "completed" ? current.current : null;
        setHistory(confirmed ? [confirmed, ...rows.filter(row => row.request_id !== confirmed.request_id)].slice(0, 30) : rows);
        setHistoryError(false);
      }
      return rows;
    } catch { if (live(gen)) setHistoryError(true); return []; }
  }, [live]);
  const recover = useCallback(async (request, gen) => {
    const { data } = await api.get(`/casino/crash/round/${encodeURIComponent(request.request_id)}`);
    const value = accept(data, gen);
    if (value?.status === "prepared") throw new Error("Flight not yet confirmed");
    if (value?.status === "running" && request.exit_requested && live(gen)) setSyncError("Your cash-out is unconfirmed. Resume the same cash-out.");
    return value;
  }, [accept, live]);
  const connect = useCallback(async () => {
    const gen = generation.current;
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/casino/crash/config");
      if (!Number.isFinite(data.cash_edge_pct) || data.cash_edge_pct < 0 || data.cash_edge_pct > 50 || data.curve_rate !== .18) throw new Error();
      if (live(gen)) setConfig(data);
    } catch { if (live(gen)) { setConfig(null); setError("Couldn’t load Crash. Reconnect before launching."); } }
    if (!live(gen)) return;
    try {
      if (pending.current) await recover(pending.current, gen);
      else {
        const { data } = await api.get("/casino/crash/active");
        if (data.round && live(gen)) { activeId.current = data.round.request_id; accept(data.round, gen); }
        else if (live(gen)) { setSyncError(""); try { sessionStorage.removeItem(key); } catch { /* recovery is read-only */ } }
      }
    } catch { if (live(gen)) setSyncError("Your flight is unconfirmed. Reconnect or resume the same flight before launching another."); }
    // Historical returns belong in history; reopening the page must not replay
    // an old successful flight or block launching on its cosmetic remainder.
    await loadHistory();
    if (live(gen)) setLoading(false);
  }, [accept, key, live, loadHistory, recover]);
  useEffect(() => {
    generation.current += 1; pending.current = null; current.current = null; activeId.current = null; lock.current = false; balanceSignature.current = "";
    setRound(null); setHistory([]); setConfig(null); setBusy(false); setSyncError("");
    try {
      const saved = JSON.parse(sessionStorage.getItem(key));
      if (saved && !validPending(saved)) throw new Error();
      pending.current = saved; activeId.current = saved?.request_id || null;
    } catch { setSyncError("Couldn’t read your saved flight. Reconnect to recover from the server."); }
    connect();
    return () => { generation.current += 1; };
  }, [connect, key]);

  const pollingId = round?.request_id, pollingStatus = round?.status;
  useEffect(() => {
    if (!pollingId || !["running", "deciding", "settling"].includes(pollingStatus)) return undefined;
    const gen = generation.current, request = { request_id: pollingId };
    let cancelled = false, timer;
    const poll = async () => {
      if (cancelled || !live(gen)) return;
      try { await recover(pending.current || request, gen); }
      catch { if (!cancelled && live(gen)) setSyncError("Connection interrupted. Your flight is still on the server. Checking the same flight…"); }
      if (!cancelled && live(gen) && current.current?.status !== "completed") timer = setTimeout(poll, 500);
    };
    const wake = () => { if (!document.hidden) { clearTimeout(timer); poll(); } };
    timer = setTimeout(poll, 500); document.addEventListener("visibilitychange", wake);
    return () => { cancelled = true; clearTimeout(timer); document.removeEventListener("visibilitychange", wake); };
  }, [pollingId, pollingStatus, live, recover]);

  const send = useCallback(async (request, exit = false) => {
    if (lock.current) return null;
    const gen = generation.current; lock.current = true; setBusy(true); setError("");
    activeId.current = request.request_id;
    try {
      const { data } = exit ? await api.post(`/casino/crash/round/${encodeURIComponent(request.request_id)}/cashout`, {}, { timeout: 15000 }) :
        await api.post("/casino/crash", { request_id: request.request_id, bet: request.bet, mode: request.mode, target: request.target, currency: request.currency }, { timeout: 15000 });
      const accepted = accept(data, gen);
      if (live(gen) && accepted?.status === "prepared") setSyncError("Your flight is unconfirmed. Resume the same flight before launching another.");
      else if (live(gen) && exit && accepted && !["completed", "not_placed"].includes(accepted.status)) setSyncError("Your cash-out is unconfirmed. Resume the same cash-out.");
      return accepted;
    } catch (e) {
      if (live(gen)) setError(e?.response ? apiError(e.response.data?.detail) : "Connection interrupted. Checking the same flight…");
      try { return await recover(request, gen); }
      catch { if (live(gen)) setSyncError(exit ? "Your cash-out is unconfirmed. Resume the same cash-out." : "Your flight is unconfirmed. Resume the same flight before launching another."); return null; }
    } finally { if (live(gen)) { lock.current = false; setBusy(false); } }
  }, [accept, live, recover]);
  const play = useCallback(async values => {
    if (lock.current || loading || !config || config.enabled === false || syncError || pending.current || current.current && ["running", "deciding", "settling"].includes(current.current.status)) return null;
    let request;
    try {
      request = { ...values, request_id: newRequestId() };
      if (!validPending(request)) throw new Error();
      sessionStorage.setItem(key, JSON.stringify(request));
    } catch { setError("Couldn’t save your flight reference. Check your settings and browser storage before launching."); return null; }
    pending.current = request; current.current = null; setRound(null);
    return send(request);
  }, [config, key, loading, send, syncError]);
  const cashOut = useCallback(async () => {
    if (lock.current || !current.current || current.current.status === "completed") return null;
    const request = { ...(pending.current || current.current), exit_requested: true };
    pending.current = { request_id: request.request_id, bet: request.bet, currency: request.currency, mode: request.mode, target: request.target, exit_requested: true };
    try { sessionStorage.setItem(key, JSON.stringify(pending.current)); } catch { /* an exit must remain possible when storage fails */ }
    return send(pending.current, true);
  }, [key, send]);
  const resume = useCallback(async () => {
    if (lock.current || loading) return null;
    if (pending.current) return send(pending.current, !!pending.current.exit_requested);
    await connect(); return null;
  }, [connect, loading, send]);
  return { round, history, config, loading, busy, error, syncError, historyError, pending: pending.current, play, cashOut, resume, reconnect: connect, loadHistory };
}
